/**
 * /rules/settings — governance for Detection Rules v2. Admin-only.
 *
 * Two section cards:
 *   1. (Type × severity) approver matrix — inline-edit, replaces v1 free-form.
 *   2. Upstream registries — read-only in Wave 1; Wave 2 per-type agents
 *      ship Enable/Disable/Import actions.
 */

import { Navigate } from "@tanstack/react-router";
import { ShieldCheck } from "lucide-react";

import { useCurrentUser } from "~/lib/auth";
import { useRegistries } from "~/lib/rules";

import { TableSkeleton } from "~/components/TableSkeleton";
import { TypeSeverityApproversEditor } from "~/components/TypeSeverityApproversEditor";

export function RuleSettingsPage(): React.ReactElement {
    const me = useCurrentUser();
    const isAdmin = me.data?.roles.includes("admin");
    const registriesQ = useRegistries();

    if (me.data && !isAdmin) return <Navigate to="/" />;

    return (
        <section className="stack" style={{ padding: "var(--space-6)", gap: "var(--space-6)" }}>
            <header className="stack-xs">
                <h1 className="type-h1">Rule governance</h1>
                <p className="type-body-muted">
                    Approval thresholds keyed by (type, severity) plus the catalog of upstream rule sources.
                </p>
            </header>

            <div className="section-card" style={{ padding: "var(--space-5)" }}>
                <div className="cluster-xs" style={{ justifyContent: "space-between", alignItems: "baseline" }}>
                    <h2 className="type-h3">
                        <ShieldCheck size={16} style={{ verticalAlign: "middle", marginRight: 6 }} />
                        Approver thresholds
                    </h2>
                    <p className="type-caption text-text-muted">
                        How many independent approvers a (type, severity) pair requires to publish. 1–5.
                    </p>
                </div>
                <div style={{ marginTop: "var(--space-4)" }}>
                    <TypeSeverityApproversEditor />
                </div>
            </div>

            <div className="section-card" style={{ padding: "var(--space-5)" }}>
                <div className="cluster-xs" style={{ justifyContent: "space-between", alignItems: "baseline" }}>
                    <h2 className="type-h3">Upstream registries</h2>
                    <p className="type-caption text-text-muted">
                        Read-only in Wave 1. Per-type Wave 2 agents add enable/disable + import actions.
                    </p>
                </div>
                {registriesQ.isLoading ? (
                    <TableSkeleton rows={4} />
                ) : (
                    <table className="data-table" style={{ marginTop: "var(--space-3)" }}>
                        <thead>
                            <tr>
                                <th>Slug</th>
                                <th>Type</th>
                                <th>License</th>
                                <th>URL</th>
                                <th>Extractor</th>
                                <th>Enabled</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(registriesQ.data ?? []).map((r) => (
                                <tr key={r.slug}>
                                    <td>
                                        <span className="type-mono-sm">{r.slug}</span>
                                    </td>
                                    <td>
                                        <span className="chip chip--subtle">{r.type}</span>
                                    </td>
                                    <td>{r.license}</td>
                                    <td>
                                        <a href={r.url} target="_blank" rel="noreferrer" className="link">
                                            {r.url}
                                        </a>
                                    </td>
                                    <td>
                                        <code className="type-mono-sm">{r.extractor}</code>
                                    </td>
                                    <td>
                                        <span className={`status-pill ${r.enabled ? "status-pill--live" : "status-pill--paused"}`}>
                                            {r.enabled ? "enabled" : "disabled"}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </section>
    );
}
